import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Trash2, Download } from "lucide-react";
import { Loader2 } from "lucide-react";
import { FaSearch, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import TableSkeleton from "../../components/AdminComponents/career/TableSkeleton";

const CareerFormDataDetails = () => {
  const [careerForms, setCareerForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  //  Fetch data
  const fetchCareerForms = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/career-form/get-all-career`
      );
      if (res.data.success) {
        setCareerForms(res.data.data);
        setFilteredForms(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCareerForms();
  }, []);

  //  Search filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredForms(careerForms);
    } else {
      const filtered = careerForms.filter(
        (form) =>
          form.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          form.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredForms(filtered);
      setCurrentPage(1);
    }
  }, [searchTerm, careerForms]);

  //  Delete handler
  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This career form will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0BB6D9",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.delete(
            `${process.env.REACT_APP_API_URL}/career-form/delete-career/${id}`
          );
          if (res.data.success) {
            Swal.fire({
              title: "Deleted!",
              text: "Career form deleted successfully.",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
            setCareerForms(careerForms.filter((item) => item._id !== id));
          }
        } catch (error) {
          console.error("Error deleting record:", error);
          Swal.fire({
            title: "Error!",
            text: "Failed to delete career form. Please try again.",
            icon: "error",
          });
        }
      }
    });
  };

  //  Pagination logic
  const totalPages = Math.ceil(filteredForms.length / itemsPerPage);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentData = filteredForms.slice(indexOfFirst, indexOfLast);

  const handlePrevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);

  //  Page number logic with ellipsis
  const getPageNumbers = () => {
    const totalNumbers = 3;
    const totalBlocks = totalNumbers + 2;
    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    const leftBound = Math.max(2, currentPage - 1);
    const rightBound = Math.min(totalPages - 1, currentPage + 1);

    if (leftBound > 2) pages.push(1, "...");
    else for (let i = 1; i < leftBound; i++) pages.push(i);

    for (let i = leftBound; i <= rightBound; i++) pages.push(i);

    if (rightBound < totalPages - 1) pages.push("...", totalPages);
    else if (rightBound === totalPages - 1) pages.push(totalPages);

    return pages;
  };

  const handlePageChange = (page) => {
    if (page !== "..." && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  //  Skeleton Loader
  

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow-md rounded-lg p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <h1 className="md:text-3xl text-2xl font-bold text-[#0BB6D9]">
            Career Form Data
          </h1>

          {/* Search */}
          <div className="relative w-full md:w-1/3">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0BB6D9]"
            />
          </div>
        </div>

        {/* Skeleton or Table */}
        {loading ? (
          <TableSkeleton />
        ) : currentData.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            No career form data found.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#0BB6D9]/10">
                <tr className="whitespace-nowrap">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Phone</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Resume</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentData.map((form, index) => (
                  <tr
                    key={form._id}
                    className={`${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-gray-100 transition whitespace-nowrap`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-800">{form.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{form.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{form.phone}</td>
                    <td className="px-4 py-3 text-sm text-blue-600">
                      {form.resume ? (
                        <a
                          href={form.resume}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:text-blue-800 underline"
                        >
                          <Download size={16} />
                          Download
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDelete(form._id)}
                        className="text-red-600 hover:text-red-800 transition p-2 rounded-full hover:bg-red-50"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex justify-end items-center mt-8 space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <FaChevronLeft size={14} /> Prev
            </button>

            {getPageNumbers().map((page, index) => (
              <div key={index}>
                {page === "..." ? (
                  <span className="px-3 py-2 text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      currentPage === page
                        ? "bg-[#2563eb] text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {page}
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <FaChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareerFormDataDetails;
